<template>
  <div class="channel-details-card channel-dropdown-card p-2 mb-2 d-flex gap-2">
    <!-- BASIC INFO -->
    <div class="drag-strip channel-drag-handle"
         title="Drag to reorder"
        @click.stop
    >
      <span class="drag-icon">⋮⋮</span>
    </div>
    <div class="channel-card-body flex-grow-1 min-w-0">
      <div class="d-flex flex-wrap align-items-center gap-3 mb-2 small">
        <div class="user-info-item"><strong>Channel details:</strong></div>
        <div class="user-info-item"><strong>ID:</strong> {{ channel.channelId }}</div>
        <span v-if="role" class="channel-role" :class="role === 'MAIN' ? 'main' : 'backup'" > {{ role }} </span>
        <div class="user-info-item"><strong>Name:</strong> {{ channel.channelName }}</div>
        <div class="user-info-item"><strong>Order Number:</strong> {{ channel.orderNumber }}</div>
        <div v-if="channel.status" class="user-info-item"><strong>Live views:</strong> {{ channel.liveViews }}</div>
        <div class="user-info-item"><strong>Status:</strong> {{ channel.status }}</div>
        <div class="user-info-item d-inline-flex align-items-center gap-2">
          <strong>Active:</strong> <input type="checkbox" :checked="channel.active" :disabled="isRunning" @change="onToggleActive" />
        </div>

        <div class="ms-auto d-flex gap-2 align-items-center">
          <button v-if="isRunning" class="procricket-btn-stop btn-xs danger" @click.stop="onStop" title="Stop stream" >
            Stop
          </button>

          <!-- START (if not running) -->
          <button v-else class="procricket-btn-start btn-xs" :disabled="!canStart" :title="startDisabledTitle" @click.stop="onStart" >
            Start
          </button>
          <button class="btn-xs icon-btn" @click="openDashboard" title="Open Kibana Dashboard">
            <img src="@/assets/chart-line.svg" alt="" class="btn-icon" />
          </button>

          <button class="btn-xs icon-btn d-inline-flex align-items-center gap-1" @click="reloadEventMetadata" >
            <img src="@/assets/reload.svg" alt="" class="btn-icon" />
            <span>Event</span>
          </button>

          <button class="btn-xs icon-btn" title="Open setting for channel metadata" @click="openSettings" :disabled="isRunning">
            <img src="@/assets/settings.svg" alt="" class="btn-icon" />
          </button>

        </div>
      </div>

      <!-- METADATA -->
      <div class="box-outline p-2">
        <div v-if="parsedMetadata.encoder" class="box-outline">
          <div class="stream-row">

            <div class="d-flex flex-wrap align-items-center gap-2">
              <span class="me-2">Encoder details:</span>

              <strong>{{ parsedMetadata.encoder.toUpperCase() }}</strong>

              <span class="divider">|</span>

              <span class="label">Stream Name:</span>
              <strong class="value">{{ parsedMetadata.name || '—' }}</strong>

              <span class="divider">|</span>

              <span class="label">Stream ID:</span>
              <strong class="value">{{ parsedMetadata.id || '—' }}</strong>

              <span class="divider">|</span>

              <span class="label">Status:</span>
              <strong class="status-pill" :class="statusClass(parsedMetadata.status)">
                {{ parsedMetadata.status || 'unknown' }}
              </strong>

              <span class="label">Last updated at:</span>
              <strong class="value">{{ parsedMetadata.timestamp || '—' }}</strong>
              <button class="btn-xs icon-btn" title="Reload stream metadata from encoder" @click="reloadStream(parsedMetadata.encoder, parsedMetadata)">
                <img src="@/assets/reload.svg" alt="" class="btn-icon" />
                <span> Channel</span>
              </button>

            </div>

            <!-- ROW 2+3 REPLACED WITH A 2-COLUMN LAYOUT -->
            <div class="row g-2 mt-1 align-items-start">
              <!-- LEFT: INPUTS -->
              <div class="col-12 col-lg-7">
                <div class="stream-line">
                  <span class="label">Input HLS URL:</span>
                </div>

                <div v-if="parsedMetadata.inputs?.length">
                  <div v-for="(inp, idx) in parsedMetadata.inputs" :key="idx" class="d-flex align-items-center gap-2 io-line" >
                    <span class="label">{{ idx + 1 }}:</span>
                    <span class="value text-truncate flex-grow-1">{{ truncateText(inp, 100) }}</span>
                  </div>
                </div>

                <div v-else class="text-muted">none</div>
                <br>
                <div class="stream-line">
                  <span class="label">Output HLS URL:</span>
                </div>
                <span class="label">1: </span>
                <span class="value text-truncate flex-grow-1">{{ parsedMetadata.output }}</span>
              </div>
            </div>

            <!-- VALIDATION ERROR MESSAGE -->
            <div v-if="validationError" class="alert alert-warning mt-2 mb-0 small">
              <strong>⚠️ Configuration Issue:</strong> {{ validationError }}
            </div>
          </div>
        </div>

        <div v-else class="text-muted mt-1">
          No stream metadata available
        </div>
      </div>
    </div>

  </div>
</template>


<script>
import { parseMetadata, validateChannelStreamName } from "@/utils/bapi-functions";
import { truncateText } from "@/utils/general-functions";

export default {
  name: 'EventChannelCard',
  props: {
    channel: {
      type: Object,
      required: true
    },
    event: {
      type: Object,
      required: true
    },
    role: {
      type: String,
      default: null
    }
  },

  methods: {
    truncateText,
    reloadStream(encoder, stream) {
      this.$emit('reload-stream', {
        channelId: this.channel.channelId,
        encoder,
        stream
      })
    },
    reloadEventMetadata() {
      this.$emit('reload-event')
    },
    onToggleActive(e) {
      this.$emit('toggle-active', {
        channelId: this.channel.channelId,
        active: e.target.checked,
      });
    },
    statusClass(status) {
      if (!status) return 'status-unknown'

      switch (status.toLowerCase()) {
        case 'running':
          return 'status-running'
        case 'stopped':
          return 'status-stopped'
        default:
          return 'status-unknown'
      }
    },
    onStart() {
      this.$emit("start-channel",  this.channel);
    },
    openSettings() {
      this.$router.push({
        name: "EventBroadcast",
        params: { id: this.event.eventId, ...this.event },
        query: { channelId: this.channel.channelId }
      });
    },

    onStop() {
      this.$emit("stop-channel", this.channel);
    },
    openDashboard() {
      const channelName = this.channel.channelName || '';
      const template = process.env.VUE_APP_KIBANA_DASHBOARD_URL_TEMPLATE || '';
      if (!template) {
        console.warn('Missing VUE_APP_KIBANA_DASHBOARD_URL_TEMPLATE');
        return;
      }
      const safe = String(channelName).replace(/"/g, '\\"').replace(/'/g, "\\'");
      const url = template.replace( `channelName : ""`, `channelName : "${safe}"` );
      window.open(url, '_blank');
    }
  },
  computed: {
    parsedMetadata() {
      return parseMetadata(this.channel.providersMetadata);
    },

    isRunning() {
      const runningFromMeta = this.parsedMetadata?.running;
      if (typeof runningFromMeta === "boolean") return runningFromMeta;
      return this.channel?.status === true;
    },

    streamValidation() {
      return validateChannelStreamName(this.channel);
    },

    // Only show error if stream is NOT running
    validationError() {
      if (this.isRunning) return null;
      return this.streamValidation.isValid ? null : this.streamValidation.error;
    },

    canStart() {
      const isActive = this.channel?.active === true || this.channel?.active === "true";
      const isValid = this.streamValidation.isValid;
      return isActive && isValid && !this.isRunning;
    },

    startDisabledTitle() {
      if (this.isRunning) return "Stream is already running.";
      if (!(this.channel?.active === true || this.channel?.active === "true")) {
        return "Channel is not active.";
      }
      if (!this.streamValidation.isValid) {
        return this.streamValidation.error;
      }
      return "";
    },
  },
};
</script>

<style src="@/assets/style.css"></style>
